/**
 * Client for the Helm agent API.
 *
 * Kept separate from `client.ts` because a turn is a stream, not a request:
 * the browser needs tokens, tool calls and confirmations as they happen, and
 * folding that into the JSON client would blur two quite different shapes.
 */

import { API_BASE_URL } from '../env'
import { withRetry } from '../../src/lib/assistant/delivery'
import type { PageContext } from '../../src/lib/assistant/session'

export type AgentMessageRole = 'USER' | 'ASSISTANT' | 'SYSTEM' | 'TOOL'

export interface AgentMessage {
  id: string
  thread_id: string
  role: AgentMessageRole
  content: string
  references: Array<Record<string, unknown>>
  run_id: string | null
  created_at: string
}

export interface AgentToolCall {
  id: string
  run_id: string | null
  tool_name: string
  arguments: Record<string, unknown> | null
  result: Record<string, unknown> | null
  status: 'PENDING' | 'AWAITING_CONFIRMATION' | 'EXECUTED' | 'REJECTED' | 'FAILED'
  error: string | null
  created_at: string
}

export interface AgentConfirmation {
  id: string
  tool_call_id: string
  target_type: string
  target_id: string | null
  summary: string
  diff: Record<string, unknown> | null
  external_effects: Array<Record<string, unknown>>
  requires_second_confirmation: boolean
  confirmed_at: string | null
  rejected_at: string | null
  created_at: string
}

export interface AgentAttachment {
  id: string
  thread_id: string
  message_id: string | null
  file_name: string
  content_type: string
  file_size_bytes: number
  extraction: Record<string, unknown> | null
  document_id: string | null
  created_at: string
}

export interface AgentThread {
  id: string
  title: string | null
  status: 'OPEN' | 'ARCHIVED'
  context_type: string | null
  context_id: string | null
  last_message_at: string | null
  created_at: string
}

export type AgentProposalStatus =
  'PENDING' | 'EXECUTING' | 'EXECUTED' | 'DISMISSED' | 'FAILED' | 'CONFLICT' | 'EXPIRED'

/** One field a proposal changes: what it was when proposed, and what it would become. */
export interface AgentProposalChange {
  field: string
  label: string
  before: unknown
  after: unknown
  before_text: string
  after_text: string
  edit?: {
    kind: 'text' | 'textarea' | 'number' | 'datetime' | 'select' | 'boolean'
    options?: Array<{ value: string; label: string }>
  }
}

/** An action Beacon proposed; nothing happens until its owner approves it. */
export interface AgentProposal {
  id: string
  thread_id: string
  run_id: string | null
  batch_id: string
  position: number
  action: string
  title: string
  target_type: string
  target_id: string | null
  target_label: string
  target_path: string | null
  summary: string
  changes: AgentProposalChange[]
  effects: string[]
  reason: string | null
  status: AgentProposalStatus
  result: Record<string, unknown> | null
  error: string | null
  conflict: Array<{
    field: string
    label: string
    proposed_from: string
    now: string
    proposed_to: string
  }> | null
  editable: string[]
  expires_at: string
  decided_at: string | null
  executed_at: string | null
  created_at: string
}

export interface AgentThreadDetail extends AgentThread {
  /** The model that last answered on this thread, from the run record. */
  model: string | null
  messages: AgentMessage[]
  tool_calls: AgentToolCall[]
  confirmations: AgentConfirmation[]
  /** Actions Beacon proposed in this conversation. */
  proposals?: AgentProposal[]
}

export type AgentEvent =
  | { type: 'run.started'; run_id: string }
  | { type: 'text.delta'; delta: string }
  | { type: 'tool.started'; tool_call_id: string; tool_name: string; arguments: unknown }
  | { type: 'tool.completed'; tool_call_id: string; tool_name: string; result: unknown }
  | { type: 'tool.failed'; tool_call_id: string; tool_name: string; error: string }
  | {
      type: 'confirmation.required'
      tool_call_id: string
      tool_name: string
      confirmation: AgentConfirmation
    }
  | { type: 'run.completed'; run_id: string; message_id: string }
  | { type: 'run.failed'; run_id: string; error: string }
  /** Beacon named the conversation, after its first reply or once the topic is clear. */
  | { type: 'thread.titled'; thread_id: string; title: string }

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  })
  // A gateway's error page is not JSON; say what happened rather than how it failed to parse.
  const body = (await response.json().catch(() => null)) as {
    success: boolean
    data?: T
    error?: { message: string }
  } | null
  if (!response.ok || !body?.success) {
    throw new Error(body?.error?.message ?? `Beacon could not answer (HTTP ${response.status}).`)
  }
  return body.data as T
}

/** A read is tried again after a dropped connection; a write is sent once. */
function json<T>(path: string, init: RequestInit = {}): Promise<T> {
  return (init.method ?? 'GET') === 'GET'
    ? withRetry(() => request<T>(path, init))
    : request<T>(path, init)
}

export const agentApi = {
  listThreads: () => json<AgentThread[]>('/agent/threads'),

  createThread: (input: { title?: string; context_type?: string; context_id?: string } = {}) =>
    json<AgentThread>('/agent/threads', { method: 'POST', body: JSON.stringify(input) }),

  listAttachments: (threadId: string) =>
    json<AgentAttachment[]>(`/agent/threads/${threadId}/attachments`),

  /**
   * Uploads one file. Sends FormData without a Content-Type header so the
   * browser sets the multipart boundary - setting it by hand produces a body
   * the server cannot parse.
   */
  uploadAttachment: async (threadId: string, file: File): Promise<AgentAttachment> => {
    const body = new FormData()
    body.append('file', file)
    const response = await fetch(`${API_BASE_URL}/agent/threads/${threadId}/attachments`, {
      method: 'POST',
      credentials: 'include',
      body,
    })
    const payload = await response.json().catch(() => null)
    if (!response.ok) {
      throw new Error(
        payload?.error?.message ?? `The file was not attached (HTTP ${response.status}).`,
      )
    }
    return payload.data as AgentAttachment
  },

  attachmentUrl: (attachmentId: string) =>
    `${API_BASE_URL}/agent/attachments/${attachmentId}/content`,

  renameThread: (threadId: string, title: string) =>
    json<AgentThread>(`/agent/threads/${threadId}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    }),

  getThread: (threadId: string) => json<AgentThreadDetail>(`/agent/threads/${threadId}`),

  confirmToolCall: (toolCallId: string, secondConfirmation = false) =>
    json<{ id: string; status: string; result: unknown }>(
      `/agent/tool-calls/${toolCallId}/confirm`,
      { method: 'POST', body: JSON.stringify({ second_confirmation: secondConfirmation }) },
    ),

  /**
   * Opens a voice call: sends the browser's WebRTC offer, gets OpenAI's answer
   * back. `page` is what the person has open, so "this inquiry" makes sense.
   */
  startVoice: (input: { sdp: string; thread_id?: string | null; page?: PageContext }) =>
    json<{ thread_id: string; run_id: string; call_id: string | null; model: string; sdp: string }>(
      '/agent/voice/sessions',
      { method: 'POST', body: JSON.stringify(input) },
    ),

  /** Runs a tool the voice model called; writes come back awaiting approval. */
  runVoiceTool: (input: { run_id: string; call_id: string; name: string; arguments: string }) =>
    json<{
      status: 'COMPLETED' | 'FAILED' | 'AWAITING_CONFIRMATION'
      output: string
      tool_call_id: string | null
      summary: string | null
    }>('/agent/voice/tool-calls', { method: 'POST', body: JSON.stringify(input) }),

  keepVoiceTranscript: (input: { run_id: string; role: 'user' | 'assistant'; text: string }) =>
    json<{ id: string }>('/agent/voice/transcripts', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  endVoice: (runId: string) =>
    json<{ ended: boolean }>(`/agent/voice/sessions/${runId}/end`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  /**
   * Approves a proposal, possibly edited first. Helm carries it out as you,
   * through the same route as the page; approving twice runs it once.
   */
  approveProposal: (proposalId: string, edits?: Record<string, unknown> | null) =>
    json<AgentProposal>(`/agent/proposals/${proposalId}/approve`, {
      method: 'POST',
      body: JSON.stringify(edits ? { edits } : {}),
    }),

  dismissProposal: (proposalId: string) =>
    json<AgentProposal>(`/agent/proposals/${proposalId}/dismiss`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  rejectToolCall: (toolCallId: string) =>
    json<{ id: string; status: string }>(`/agent/tool-calls/${toolCallId}/reject`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  /**
   * Streams a turn.
   *
   * Uses fetch rather than EventSource because the turn is a POST carrying the
   * message, and EventSource only issues GETs. `signal` lets the composer stop
   * a run mid-flight. `page` is the page the person is on when they ask.
   */
  async streamMessage(
    threadId: string,
    content: string,
    onEvent: (event: AgentEvent) => void,
    signal?: AbortSignal,
    page?: PageContext | null,
  ): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/agent/threads/${threadId}/messages`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(page ? { content, page } : { content }),
      signal,
    })

    if (!response.ok || !response.body) {
      const body = (await response.json().catch(() => null)) as {
        error?: { message?: string }
      } | null
      throw new Error(body?.error?.message ?? `Beacon could not answer (HTTP ${response.status}).`)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // SSE frames are separated by a blank line; anything after the last one
      // is a partial frame and stays buffered.
      const frames = buffer.split('\n\n')
      buffer = frames.pop() ?? ''

      for (const frame of frames) {
        const line = frame.split('\n').find((part) => part.startsWith('data: '))
        if (!line) continue
        const payload = line.slice(6)
        if (payload === '[DONE]') return
        try {
          onEvent(JSON.parse(payload) as AgentEvent)
        } catch {
          // A malformed frame should not kill an otherwise working stream.
        }
      }
    }
  },
}
